import { Component, inject, viewChild, ElementRef, AfterViewInit, OnInit, signal, OnDestroy } from '@angular/core';

import { ReactiveFormsModule, FormsModule, Validators, FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { PopoverPositions } from '@vendasta/galaxy/popover';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BreakpointObserver } from '@angular/cdk/layout';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyRichTextEditorModule } from '@vendasta/galaxy/rich-text-editor';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  SendApiService,
  SendFromProviderRequest,
  Provider,
  UserParticipant,
  ContactParticipant,
  WhiteLabelEmailMessage,
  Visibility,
  SourceInfo
} from '@vendasta/email';
import { DEFAULT_RICH_TEXT_PLUGINS, DEFAULT_RICH_TEXT_TOOLBAR, EmailProvider, EMAIL_PROVIDER_OUTLOOK } from './constants';
import { EmailComposerData } from '../email-workflow.service';
import { SendEmailService } from './send-email.service';

interface ContactInfo {
  id: string;
  name: string;
  email: string;
}

@Component({
  selector: 'lib-crm-send-email',
  templateUrl: './send-email.component.html',
  styleUrls: ['./send-email.component.scss'],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    GalaxyFormFieldModule,
    GalaxyTooltipModule,
    GalaxyRichTextEditorModule,
    GalaxyButtonLoadingIndicatorModule
],
})
export class CrmSendEmailComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly dialogRef = inject(MatDialogRef<CrmSendEmailComponent>);
  private readonly dialogData = inject<EmailComposerData>(MAT_DIALOG_DATA);
  private readonly snackbarService = inject(SnackbarService);
  private readonly confirmationModalService = inject(OpenConfirmationModalService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly fb = inject(FormBuilder);
  private readonly sendEmailService = inject(SendApiService);
  private readonly draftService = inject(SendEmailService);
  private readonly router = inject(Router);

  // Visibility options for the dropdown
  protected readonly visibilityOptions = [
    { value: Visibility.VISIBILITY_PRIVATE, label: 'CRM.VISIBILITY_PRIVATE' },
    { value: Visibility.VISIBILITY_INTERNAL, label: 'CRM.VISIBILITY_INTERNAL' }
  ];

  protected readonly tooltipPositions = [PopoverPositions['Right'], PopoverPositions['Top']];

  // Signal-based viewChild for programmatic focus
  readonly subjectInput = viewChild<ElementRef<HTMLInputElement>>('subjectInput');

  // Rich text editor constants
  protected readonly DEFAULT_RICH_TEXT_PLUGINS = DEFAULT_RICH_TEXT_PLUGINS;
  protected readonly DEFAULT_RICH_TEXT_TOOLBAR = DEFAULT_RICH_TEXT_TOOLBAR;

  // Signal-based reactive state
  protected readonly showErrors = signal(false);
  protected readonly isLoading = signal(false);
  protected readonly isSending = signal(false);
  protected readonly contacts = signal<ContactInfo[]>([]);
  private emailSent = false; // Track if email was sent to avoid saving draft
  private formSubscription?: Subscription;
  private readonly destroy$ = new Subject<void>();
  private readonly MOBILE_BREAKPOINT = '(max-width: 599px)';

  protected readonly emailForm = this.fb.nonNullable.group({
    to: ['', [Validators.required]],
    subject: ['', [Validators.required]],
    content: ['', [Validators.required]],
    visibility: [Visibility.VISIBILITY_INTERNAL]
  });

  constructor() {
    // Load contacts when dialog data is available
    if (this.dialogData?.contactIds?.length) {
      this.loadAndPopulateContacts(this.dialogData.contactIds, this.dialogData.emails || []);
    }
  }

  ngOnInit(): void {
    // Set up responsive dialog - watch for breakpoint changes
    this.breakpointObserver
      .observe(this.MOBILE_BREAKPOINT)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        console.log('Breakpoint changed:', state.matches ? 'mobile' : 'desktop');
        this.updateDialogSize(state.matches);
      });
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();
    
    // Save draft when component is destroyed (dialog closes)
    this.saveDraftIfNeeded();
    this.formSubscription?.unsubscribe();
  }

  ngAfterViewInit() {
    // Focus on subject field after view initializes
    const inputElement = this.subjectInput();
    if (inputElement) {
      setTimeout(() => {
        inputElement.nativeElement.focus();
      }, 0);
    }
  }

  private updateDialogSize(isMobile: boolean): void {
    if (isMobile) {
      // Mobile: full screen
      this.dialogRef.updateSize('100vw', '100vh');
    } else {
      // Desktop: normal size - also update position to center
      this.dialogRef.updateSize('1000px', '95vh');
      // Force reposition to center when transitioning from mobile to desktop
      this.dialogRef.updatePosition();
    }
  }

  private async loadAndPopulateContacts(contactIds: string[], emailAddresses: string[]): Promise<void> {
    try {
      this.isLoading.set(true);

      // Build contact list with actual email addresses from CRM
      this.contacts.set(contactIds.map((id, index) => ({
        id,
        name: `Contact ${index + 1}`,
        email: emailAddresses[index] || ''
      })));

      // Pre-populate the "to" field with valid contact emails
      const validEmails = this.contacts()
        .map(contact => contact.email)
        .filter(email => email && email.trim() !== '');

      // Check if any contacts have email addresses
      if (validEmails.length === 0) {
        // Show error modal and close dialog after confirmation
        await firstValueFrom(
          this.confirmationModalService.openModal({
            type: 'warn',
            title: 'CRM.NO_EMAIL_ADDRESS_TITLE',
            message: 'CRM.NO_EMAIL_ADDRESS_MESSAGE',
            confirmButtonText: 'COMMON.ACTION_LABELS.CLOSE',
            cancelButtonText: undefined, // Hide cancel button for error modal
            cancelOnEscapeKeyOrBackgroundClick: true,
          })
        );
        this.closeModal();
        return;
      }

      const toValue = validEmails.join(', ');
      this.emailForm.patchValue({
        to: toValue
      });

      // Check for draft and ask to restore
      await this.checkAndRestoreDraft(toValue);
    } catch (error) {
      console.error('Error loading contacts:', error);
      this.snackbarService.openErrorSnack('CRM.ERROR_LOADING_CONTACTS');
    } finally {
      this.isLoading.set(false);
    }
  }

  private async checkAndRestoreDraft(to: string): Promise<void> {
    const draft = this.draftService.getDraft(to);
    if (!draft) {
      return;
    }

    // Ask user if they want to restore the draft
    const shouldRestore = await firstValueFrom(this.draftService.askToRestoreDraft(to));

    if (shouldRestore) {
      this.emailForm.patchValue({
        subject: draft.subject,
        content: draft.content
      });
    } else {
      // User chose to discard, delete the draft
      this.draftService.deleteDraft(to);
    }
  }

  private saveDraftIfNeeded(): void {
    // Don't save draft if email was sent
    if (this.emailSent) {
      return;
    }

    const formValue = this.emailForm.value;
    const to = formValue.to;
    const subject = formValue.subject || '';
    const content = formValue.content || '';

    // Only save if there's content worth saving
    if (to && (subject.trim() || content.trim())) {
      this.draftService.saveDraft(to, subject, content);
    }
  }

  protected async onSend() {
    if (!this.emailForm.valid) {
      this.showErrors.set(true);
      this.emailForm.markAllAsTouched();
      return;
    }
    if (!this.isSending()) {
      try {
        this.isSending.set(true);

        const formValue = this.emailForm.value;
        await this.sendEmailWithProvider(
          formValue.to,
          formValue.subject,
          formValue.content,
          this.dialogData.provider,
          formValue.visibility
        );

        // Mark email as sent and clear draft
        this.emailSent = true;
        if (formValue.to) {
          this.draftService.deleteDraft(formValue.to);
        }

        this.snackbarService.openSuccessSnack('CRM.EMAIL_SENT_SUCCESS');
        // Close the modal
        this.closeModal();
      } catch (error) {
        console.error('Error sending email:', error);
        this.snackbarService.openErrorSnack('CRM.ERROR_SENDING_EMAIL');
      } finally {
        this.isSending.set(false);
      }
    }
  }

  private async sendEmailWithProvider(to?: string, subject?: string, content?: string, provider?: EmailProvider, visibility?: Visibility): Promise<void> {
    try {
      // Parse recipients from the email data
      const toEmails = to?.split(',').map((email: string) => email.trim()) || [];

      // Send email to each recipient using sendFromProvider
      for (const recipientEmail of toEmails) {
        if (recipientEmail) {
          const request = this.buildSendFromProviderRequest(
            recipientEmail,
            this.dialogData.namespace,
            this.dialogData.userId,
            subject || '',
            content || '',
            provider as EmailProvider,
            visibility ?? Visibility.VISIBILITY_PRIVATE
          );
          await firstValueFrom(this.sendEmailService.sendFromProvider(request));
        }
      }
    } catch (error) {
      console.error('Failed to send email via sendFromProvider:', error);
      throw error;
    }
  }

  private buildSendFromProviderRequest(recipientEmail: string, namespace: string, currentUserId: string,
    subject: string, htmlContent: string, provider: EmailProvider, visibility: Visibility): SendFromProviderRequest {
    // Extract contact ID from the contacts array if available
    const contact = this.contacts().find(c => c.email === recipientEmail);
    const contactId = contact?.id || recipientEmail;
    // Create UserParticipant instance
    const userParticipant = new UserParticipant({
      userId: currentUserId
    });
    // Create ContactParticipant instance
    const contactParticipant = new ContactParticipant({
      contactId: contactId,
      namespace: namespace,
    });
    // Create WhiteLabelEmailMessage instance
    const message = new WhiteLabelEmailMessage({
      subject: subject,
      htmlContent: htmlContent,
      attributes: [],
      links: [],
      attachments: [],
      urlAttachments: []
    });
    // Create SourceInfo instance
    const sourceInfo = new SourceInfo({
      sourceName: 'CRM UI',
      sourceDrillDown1: this.router.url,
    });

    // Create and return the request
    return new SendFromProviderRequest({
      namespace: namespace,
      provider: provider === EMAIL_PROVIDER_OUTLOOK ? Provider.PROVIDER_OUTLOOK : Provider.PROVIDER_GMAIL,
      userParticipant: userParticipant,
      contactParticipant: contactParticipant,
      message: message,
      sendAsDraft: false,
      providerMessageThreadId: undefined,
      visibility: visibility,
      sourceInfo: sourceInfo
    });
  }

  onCancel() {
    // Save draft before closing
    this.saveDraftIfNeeded();
    this.closeModal();
  }

  private closeModal() {
    this.dialogRef.close();
  }
}

